import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import moment from 'moment';
import { Subscription } from 'rxjs';
import { objectToCamel } from 'ts-case-convert';
import {
  IAvailabilities,
  IBooking,
  IRouteDetails,
  IRouteReorganizationBookingResult,
  IRouteReorganizationDeparture,
  IRouteReorganizationDraft,
  IRouteReorganizationRequest,
  IRouteReorganizationSourceSchedule,
  IRouteSetup,
  IRouteSetupDetail,
  IReservationRestrictionWarning,
  IServiceInfoDetail,
  IServiceWizardSetupPayload,
} from 'src/app/models';
import {
  NotificationTypes,
  RefundOptions,
  ReservationFailureReason,
  RouteReorganizationType,
  ServiceType,
  ServiceWizardStep,
  ScheduleWindowIssue,
} from 'src/app/models/enums';
import { ErrorsCode } from 'src/app/models/enums/errorsCode';
import {
  BookingsService,
  LiteralService,
  RouteReorganizationService,
  RouteService,
  ServiceWizardService,
  StorageService,
  UtilsService,
  WebSocketService,
} from 'src/app/services';
import { IRouteReorganizationDepartureOption } from 'src/app/services/route-reorganization/route-reorganization.service';
import { ServiceInfoService } from 'src/app/services/service-info/service-info.service';
import { APP } from 'src/app/config/app';
import { images } from 'src/images';

@Component({
  selector: 'app-route-reorganization',
  templateUrl: './route-reorganization.component.html',
  styleUrl: './route-reorganization.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class RouteReorganizationComponent implements OnInit, OnDestroy {
  public images = images;
  public RouteReorganizationType = RouteReorganizationType;
  public readonly APP = APP;
  public readonly ScheduleWindowIssue = ScheduleWindowIssue;
  public CANCEL_BOOKING = APP.REORGANIZATION_CANCEL_BOOKING;
  public refundOptions = Object.values(RefundOptions);

  public serviceInfoId: number;
  public serviceInfo?: IServiceInfoDetail;
  public routes: IRouteDetails[] = [];
  public summary = false;

  public type = RouteReorganizationType.MERGE;
  public selectedRouteIds: number[] = [];
  public routesCount = APP.REORGANIZATION_MIN_SPLIT_ROUTES;

  public simulation?: Awaited<
    ReturnType<RouteReorganizationService['simulate']>
  >;
  public setups: (IServiceWizardSetupPayload | null)[] = [];
  public newRoutes: any[] = [];
  public recomputePrice = false;
  public restrictionWarnings: IReservationRestrictionWarning[] = [];
  public restrictionsAccepted = false;

  public loading = false;
  public applying = false;
  public progressPercentage = 0;
  public progressDetail = '';

  private wizardSubscriptions: Subscription[] = [];

  constructor(
    public literalService: LiteralService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private serviceInfoService: ServiceInfoService,
    private routeService: RouteService,
    private bookingsService: BookingsService,
    private routeReorganizationService: RouteReorganizationService,
    private serviceWizardService: ServiceWizardService,
    private storageService: StorageService,
    private utilsService: UtilsService,
    private webSocketService: WebSocketService,
  ) {}

  get draft(): IRouteReorganizationDraft | null {
    return this.routeReorganizationService.draft;
  }

  async ngOnInit() {
    this.serviceInfoId = Number(
      this.activatedRoute.snapshot.paramMap.get('id'),
    );
    this.summary = this.activatedRoute.snapshot.data['summary'] === true;
    const draft =
      this.draft?.serviceInfoId === this.serviceInfoId ? this.draft : null;
    if (this.summary && !draft?.simulation) {
      this.router.navigate(['/services', this.serviceInfoId, 'reorganize'], {
        replaceUrl: true,
      });
      return;
    }
    if (draft) {
      this.type = draft.type;
      this.selectedRouteIds = draft.sourceSchedules.map(
        (schedule) => schedule.route.id!,
      );
      if (draft.type === RouteReorganizationType.SPLIT)
        this.routesCount = this.serviceWizardService.reorganizationRoutesCount;
    }
    this.activateWizard();
    this.loading = true;
    try {
      this.serviceInfo = (await this.serviceInfoService.getServiceInfo(
        this.serviceInfoId,
      )) as IServiceInfoDetail;
      this.updateBreadcrumb();
      if (this.summary) await this.loadSummary();
      else await this.loadRoutes();
    } finally {
      this.loading = false;
    }
  }

  ngOnDestroy() {
    this.wizardSubscriptions.forEach((subscription) =>
      subscription.unsubscribe(),
    );
    this.storageService.clearBreadcrumb();
    this.serviceWizardService.stop();
  }

  private activateWizard() {
    const wizard = this.serviceWizardService;
    if (wizard.reorganization !== this.type || !this.draft)
      wizard.startReorganization(this.type);
    wizard.restore(
      this.summary
        ? ServiceWizardStep.REORGANIZATION_SUMMARY
        : ServiceWizardStep.REORGANIZATION_SOURCES,
    );
    wizard.canContinueEvaluator = () =>
      this.summary ? this.summaryValid && !this.applying : this.selectionValid;
    wizard.continueLabelKey = this.summary
      ? 'services.routes.reorganize.summary.apply'
      : 'continue';
    this.wizardSubscriptions = [
      wizard.continueEventEmitter.subscribe(() =>
        this.summary ? this.apply() : this.startDesign(),
      ),
      wizard.backEventEmitter.subscribe(() => this.back()),
    ];
  }

  private updateBreadcrumb() {
    this.storageService.setBreadcrumb([
      { labelKey: 'sidebar.services', url: '/services' },
      {
        label: this.serviceInfo?.tag ?? '',
        url: `/services/${this.serviceInfoId}`,
      },
      { labelKey: 'services.routes.reorganize.title' },
    ]);
  }

  private async loadRoutes() {
    const { content } = await this.routeService.getRoutesByServiceInfo(
      [this.serviceInfoId],
      1,
      APP.REORGANIZATION_MAX_ITEMS,
    );
    this.routes = await Promise.all(
      (content as { id: number }[]).map(
        async (route) =>
          (await this.routeService.getService(route.id)) as IRouteDetails,
      ),
    );
  }

  get selectionValid(): boolean {
    return this.routeReorganizationService.isSelectionValid(
      this.type,
      this.selectedRouteIds,
    );
  }

  changeType(type: RouteReorganizationType) {
    this.type = type;
    this.selectedRouteIds = [];
    this.routesCount = APP.REORGANIZATION_MIN_SPLIT_ROUTES;
    this.restartWizard();
  }

  get maxRoutesCount(): number {
    const route = this.routes.find(
      (candidate) => candidate.id === this.selectedRouteIds[0],
    );
    return route
      ? this.routeReorganizationService.maxSplitRoutes(route)
      : APP.REORGANIZATION_MIN_SPLIT_ROUTES;
  }

  changeRoutesCount(count: number) {
    const routesCount = Math.min(
      Math.max(
        Math.round(Number(count) || 0),
        APP.REORGANIZATION_MIN_SPLIT_ROUTES,
      ),
      this.maxRoutesCount,
    );
    if (routesCount === this.routesCount) return;
    this.routesCount = routesCount;
    this.restartWizard();
  }

  // The header shows one route and one setup step per planned new route
  private restartWizard() {
    const plannedRoutes =
      this.type === RouteReorganizationType.SPLIT
        ? Array.from({ length: this.routesCount }, () => null)
        : [];
    this.serviceWizardService.startReorganization(
      this.type,
      null,
      plannedRoutes,
    );
    this.serviceWizardService.restore(ServiceWizardStep.REORGANIZATION_SOURCES);
  }

  toggleRoute(routeId: number) {
    if (this.type === RouteReorganizationType.SPLIT) {
      this.selectedRouteIds = this.selectedRouteIds.includes(routeId)
        ? []
        : [routeId];
      if (this.routesCount > this.maxRoutesCount)
        this.changeRoutesCount(this.maxRoutesCount);
      return;
    }
    this.selectedRouteIds = this.selectedRouteIds.includes(routeId)
      ? this.selectedRouteIds.filter((id) => id !== routeId)
      : [...this.selectedRouteIds, routeId];
  }

  private get selectionUnchanged(): boolean {
    const draft = this.draft;
    if (draft?.serviceInfoId !== this.serviceInfoId || draft.type !== this.type)
      return false;
    const draftRouteIds = draft.sourceSchedules.map(
      (schedule) => schedule.route.id,
    );
    return (
      (this.type !== RouteReorganizationType.SPLIT ||
        this.serviceWizardService.reorganizationRoutes.length ===
          this.routesCount) &&
      draftRouteIds.length === this.selectedRouteIds.length &&
      this.selectedRouteIds.every((routeId) => draftRouteIds.includes(routeId))
    );
  }

  async startDesign() {
    if (!this.selectionValid) return;
    if (
      !this.selectionUnchanged ||
      !this.serviceWizardService.pendingQuickCreation
    )
      await this.prepareDraft();
    this.router.navigate(this.serviceWizardService.reorganizationRoutePath(0));
  }

  private async prepareDraft() {
    this.loading = true;
    try {
      const sourceRoutes = this.selectedRouteIds.map((routeId) =>
        this.routes.find((route) => route.id === routeId)!,
      );
      const sourceSchedules = await Promise.all(
        sourceRoutes.map((route) => this.loadSourceSchedule(route)),
      );
      const service = this.routeReorganizationService;
      const proposedStops =
        this.type === RouteReorganizationType.MERGE
          ? [service.mergedStops(sourceRoutes)]
          : service.splitStops(
              sourceRoutes[0],
              await this.loadPlannableBookings(sourceRoutes[0]),
              this.routesCount,
            );
      const maxTime = Math.max(
        ...sourceRoutes.map((route) => route.maxTime ?? 0),
      );
      const newRoutes = proposedStops.map((stops) =>
        this.newRouteDraft(sourceRoutes, stops, maxTime),
      );
      service.draft = {
        serviceInfoId: this.serviceInfoId,
        type: this.type,
        sourceSchedules,
        ticketing: service.hasTicketing(sourceRoutes),
        roundTrip: !!this.serviceInfo?.roundTrips,
        simulation: null,
        cancellations: [],
        cancellationOptions: {
          refund: true,
          fullRefund: true,
          cancelRelated: true,
        },
        sendNotification: true,
        assignments: [],
        setupsProposed: false,
      };
      this.serviceWizardService.startReorganization(
        this.type,
        {
          serviceInfo: this.wizardServiceInfo(sourceRoutes[0]),
          route: newRoutes[0],
        },
        newRoutes,
      );
    } finally {
      this.loading = false;
    }
  }

  private async loadPlannableBookings(
    route: IRouteDetails,
  ): Promise<IBooking[]> {
    const { content } = await this.bookingsService.getBookingsFiltered(
      APP.REORGANIZATION_MAX_ITEMS,
      1,
      [],
      [],
      [],
      APP.REORGANIZATION_PLANNABLE_STATUS,
      false,
      false,
      [],
      [{ id: route.id! }],
      '',
      '',
    );
    return objectToCamel(content ?? []) as unknown as IBooking[];
  }

  private async loadSourceSchedule(
    route: IRouteDetails,
  ): Promise<IRouteReorganizationSourceSchedule> {
    const discretionary =
      (this.serviceInfo as any)?.serviceType === ServiceType.DISCRETIONARY;
    const setups = (await this.routeService.getRouteSetups(
      route.id!,
    )) as IRouteSetup[];
    const activeSetup = setups.find((setup) => setup.active);
    const [setup, availabilities] = await Promise.all([
      activeSetup
        ? (this.routeService.getRouteSetupsSchedule(
            route.id!,
            activeSetup.id!,
          ) as Promise<IRouteSetupDetail>)
        : Promise.resolve(null),
      discretionary
        ? this.routeService
            .getRouteAvailabilities(
              route.id!,
              1,
              APP.REORGANIZATION_MAX_ITEMS,
              undefined,
              APP.REORGANIZATION_PLANNABLE_STATUS,
            )
            .then((page: any) => (page?.content ?? []) as IAvailabilities[])
        : Promise.resolve([] as IAvailabilities[]),
    ]);
    return { route, setup, availabilities };
  }

  // The new route starts with the attributes of the first source route; its path is proposed from the stops
  private newRouteDraft(
    sourceRoutes: IRouteDetails[],
    proposedStops: any[],
    maxTime: number,
  ) {
    const { id, terrain, outline, stops, name, active, ...attributes } =
      structuredClone(sourceRoutes[0]) as any;
    const expeditionType =
      this.routeReorganizationService.expeditionTypeFor(sourceRoutes);
    return {
      ...attributes,
      ...(expeditionType ? { expeditionType } : {}),
      relatedServices:
        this.routeReorganizationService.relatedRoutesOf(sourceRoutes),
      name: '',
      maxTime,
      proposedStops,
    };
  }

  private wizardServiceInfo(route: IRouteDetails) {
    const serviceInfo: any = this.serviceInfo ?? {};
    return {
      id: this.serviceInfoId,
      name: serviceInfo.name,
      tag: serviceInfo.tag,
      color: serviceInfo.color,
      serviceType: serviceInfo.serviceType,
      roundTrips: false,
      townId: route.town?.id,
      towns: route.town ? [route.town] : [],
      clientId: route.managerClient?.id,
      clients: route.managerClient ? [route.managerClient] : [],
    };
  }

  // Summary

  private async loadSummary() {
    const wizard = this.serviceWizardService;
    this.newRoutes = wizard.reorganizationRoutes;
    this.setups = wizard.reorganizationSetups.map((setup) =>
      wizard.setupPayload(setup),
    );
    const draft = this.draft!;
    draft.assignments = this.routeReorganizationService.autoAssign(
      draft.simulation!.bookings,
      this.setups,
      draft.cancellations,
      draft.assignments,
    );
    await this.simulate();
  }

  async simulate() {
    this.loading = true;
    try {
      this.simulation = await this.routeReorganizationService.simulate(
        this.serviceInfoId,
        this.request(),
      );
    } catch (error: any) {
      this.showError(error?.error ?? error);
    } finally {
      this.loading = false;
    }
  }

  private request(): IRouteReorganizationRequest {
    const acceptedRestrictions = this.restrictionsAccepted
      ? [...new Set(this.restrictionWarnings.map((warning) => warning.reason))]
      : [];
    return this.routeReorganizationService.buildRequest(
      this.draft!,
      this.newRoutes.map((route, index) =>
        this.routeReorganizationService.newRouteRequest(
          route,
          this.setups[index],
        ),
      ),
      {
        recomputePrice: this.draft!.ticketing && this.recomputePrice,
        acceptedRestrictions:
          acceptedRestrictions as ReservationFailureReason[],
      },
    );
  }

  routeName(newRouteIndex: number): string {
    return this.newRoutes[newRouteIndex]?.name ?? '';
  }

  sourceRouteName(routeId: number): string {
    return (
      this.draft?.sourceSchedules.find(
        (schedule) => schedule.route.id === routeId,
      )?.route.name ?? ''
    );
  }

  get bookings(): IRouteReorganizationBookingResult[] {
    return this.simulation?.bookings ?? [];
  }

  isCancelled(bookingId: number): boolean {
    return !!this.draft?.cancellations.includes(bookingId);
  }

  departureOptions(
    booking: IRouteReorganizationBookingResult,
  ): IRouteReorganizationDepartureOption[] {
    return this.routeReorganizationService.departureOptions(
      this.setups,
      booking.compatibleNewRouteIndexes,
      booking.sourceDepartureDateTime,
    );
  }

  optionKey(option: { newRouteIndex: number; departureDateTime: string }) {
    return `${option.newRouteIndex}|${option.departureDateTime}`;
  }

  optionLabel(option: IRouteReorganizationDepartureOption): string {
    return `${this.routeName(option.newRouteIndex)} · ${moment(option.departureDateTime).format('HH:mm')}`;
  }

  bookingSelection(
    booking: IRouteReorganizationBookingResult,
  ): string | undefined {
    if (this.isCancelled(booking.bookingId)) return this.CANCEL_BOOKING;
    const assignment = this.draft!.assignments.find(
      (candidate) => candidate.bookingId === booking.bookingId,
    );
    return assignment ? this.optionKey(assignment) : undefined;
  }

  async selectBookingTarget(
    booking: IRouteReorganizationBookingResult,
    selection: string,
  ) {
    const draft = this.draft!;
    draft.assignments = draft.assignments.filter(
      (assignment) => assignment.bookingId !== booking.bookingId,
    );
    draft.cancellations = draft.cancellations.filter(
      (bookingId) => bookingId !== booking.bookingId,
    );
    if (selection === this.CANCEL_BOOKING) {
      draft.cancellations = [...draft.cancellations, booking.bookingId];
    } else {
      const option = this.departureOptions(booking).find(
        (candidate) => this.optionKey(candidate) === selection,
      );
      if (option)
        draft.assignments = [
          ...draft.assignments,
          { bookingId: booking.bookingId, ...option },
        ];
    }
    await this.simulate();
  }

  async autoAssign() {
    const draft = this.draft!;
    draft.assignments = this.routeReorganizationService.autoAssign(
      this.bookings,
      this.setups,
      draft.cancellations,
    );
    await this.simulate();
  }

  bookingName(booking: IRouteReorganizationBookingResult): string {
    const details: any = booking.booking ?? {};
    return (
      `${details.firstName ?? ''} ${details.lastName ?? ''}`.trim() ||
      `#${booking.bookingId}`
    );
  }

  formatDateTime(dateTime: string): string {
    return moment(dateTime).format('DD/MM/YYYY HH:mm');
  }

  isOverCapacity(departure: IRouteReorganizationDeparture): boolean {
    return (
      departure.plannedSeats > departure.seatsCapacity ||
      departure.plannedPrmSeats > departure.prmCapacity
    );
  }

  newRouteResult(newRouteIndex: number) {
    return this.simulation?.newRoutes.find(
      (newRoute) => newRoute.newRouteIndex === newRouteIndex,
    );
  }

  departuresCount(setup: IServiceWizardSetupPayload | null): number {
    return (setup?.daysOfWeek?.length ?? 0) + (setup?.departures?.length ?? 0);
  }

  missingCrew(setup: IServiceWizardSetupPayload | null): boolean {
    return (
      [...(setup?.daysOfWeek ?? []), ...(setup?.holyDays ?? [])].some(
        (row: any) => !row.driver?.id || !row.vehicle?.id,
      ) ||
      (setup?.departures ?? []).some(
        (departure: any) => !departure.driverId || !departure.vehicleId,
      )
    );
  }

  newRouteMaxPathTime(newRouteIndex: number): number | undefined {
    return (
      this.newRouteResult(newRouteIndex)?.minutesToCoverEveryStop ??
      this.newRoutes[newRouteIndex]?.maxPathTime
    );
  }

  windowIssue(newRouteIndex: number): ScheduleWindowIssue | null {
    const newRoute = this.newRoutes[newRouteIndex];
    const result = this.newRouteResult(newRouteIndex);
    const minPathTime = result?.minPathTime ?? newRoute?.minPathTime;
    const setup = this.setups[newRouteIndex];
    const issues = [
      ...(setup?.daysOfWeek ?? []),
      ...(setup?.holyDays ?? []),
    ].map((row: any) =>
      this.utilsService.scheduleWindowIssue(
        row,
        this.newRouteMaxPathTime(newRouteIndex),
        minPathTime,
      ),
    );
    return (
      issues.find(
        (issue) => issue === ScheduleWindowIssue.BELOW_MIN_PATH_TIME,
      ) ??
      issues.find((issue) => !!issue) ??
      null
    );
  }

  get unassignedCount(): number {
    return this.bookings.filter(
      (booking) =>
        !this.isCancelled(booking.bookingId) && !this.bookingSelection(booking),
    ).length;
  }

  get summaryValid(): boolean {
    if (!this.simulation || this.loading) return false;
    const setupsReady = this.setups.every(
      (setup, index) =>
        setup &&
        !this.missingCrew(setup) &&
        this.windowIssue(index) !== ScheduleWindowIssue.BELOW_MIN_PATH_TIME,
    );
    return (
      setupsReady &&
      !this.unassignedCount &&
      !this.simulation.unplannedBookingIds.length &&
      !this.simulation.bookings.some((booking) => booking.errorCode) &&
      !this.simulation.departures.some((departure) =>
        this.isOverCapacity(departure),
      ) &&
      (!this.restrictionWarnings.length || this.restrictionsAccepted)
    );
  }

  get refundOption(): RefundOptions {
    const options = this.draft!.cancellationOptions;
    if (!options.refund) return RefundOptions.NO_REFUND;
    return options.fullRefund
      ? RefundOptions.FULL_REFUND
      : RefundOptions.ACCORDING_POLICY;
  }

  set refundOption(option: RefundOptions) {
    const options = this.draft!.cancellationOptions;
    options.refund = option !== RefundOptions.NO_REFUND;
    options.fullRefund = option === RefundOptions.FULL_REFUND;
  }

  async apply() {
    if (!this.summaryValid) return;
    this.applying = true;
    this.progressPercentage = 0;
    try {
      const outcome = await this.routeReorganizationService.apply(
        this.serviceInfoId,
        this.request(),
        (message) => {
          this.progressPercentage =
            message.total_percentage ?? this.progressPercentage;
          this.progressDetail = this.webSocketService.progressDetail(message);
        },
      );
      if (outcome.trackingLost) {
        this.utilsService.showNotification(
          images.notifications.warning,
          'services.routes.reorganize.trackingLost',
          NotificationTypes.WARNING,
        );
        this.exit();
        return;
      }
      if (outcome.error?.error_code === ErrorsCode.RESTRICTIONS_NOT_ACCEPTED) {
        this.restrictionWarnings = objectToCamel(
          outcome.error.entity_info ?? [],
        ) as unknown as IReservationRestrictionWarning[];
        this.restrictionsAccepted = false;
        return;
      }
      if (outcome.error) {
        this.showError(outcome.error);
        await this.simulate();
        return;
      }
      this.utilsService.showNotification(
        images.notifications.check,
        'services.routes.reorganize.success',
        NotificationTypes.SUCCESS,
      );
      this.exit();
    } catch (error: any) {
      this.showError(error?.error ?? error);
    } finally {
      this.applying = false;
    }
  }

  private showError(error: any) {
    const key = error?.error_code
      ? `${error.error_code}_ERROR`
      : 'services.routes.reorganize.error';
    this.utilsService.showNotification(
      images.notifications.warning,
      key,
      NotificationTypes.DANGER,
    );
  }

  back() {
    if (this.summary) {
      const wizard = this.serviceWizardService;
      this.router.navigate(
        wizard.reorganizationSetupPath(wizard.reorganizationRoutesCount - 1),
      );
      return;
    }
    this.exit();
  }

  exit() {
    const returnUrl =
      this.routeReorganizationService.returnUrl ??
      `/services/${this.serviceInfoId}/routes`;
    this.routeReorganizationService.draft = null;
    this.routeReorganizationService.returnUrl = null;
    this.serviceWizardService.clear();
    this.router.navigateByUrl(returnUrl);
  }
}
